import React, {useState, useEffect} from 'react';
import { toast } from 'react-toastify';
import {Info} from 'lucide-react';
import SectionWrapper from "../../../components/Page/SectionWrapper";
import useSignupContext from "../../../context/SignupContext";
import LoaderFullScreen from "../../../components/Page/LoaderFullScreen";
import YourCart from "../../../components/Cart/YourCart";
import YourBottomCart from "../../../components/Cart/YourBottomCart";
import {deleteAddress} from "../../../services/localStorage.service";
import {useNavigate} from "react-router-dom";
import {submitOrder} from "../../../api/signup.api";
import ButtonAlternative from "../../../components/Form/Button/ButtonAlternative";
import ButtonPrimary from "../../../components/Form/Button/ButtoPrimary";

const Summary: React.FC = () => {

  const navigate = useNavigate();

  const {clearContext, cartItems, profile} = useSignupContext();

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [connectionDate, setConnectionDate] = useState<string>('');
  const [hasCurrentIsp, setHasCurrentIsp] = useState<string>('');
  const [currentIspName, setCurrentIspName] = useState<string>('');
  const [currentIspAccount, setCurrentIspAccount] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderStatus, setOrderStatus] = useState<'success' | 'error' | null>(null);

  const resetAddress = () => {
    // Clear context data
    clearContext();

    // Delete address cache
    deleteAddress();

    navigate('/address-select');
  };

  const onSubmitOrder = async () => {
    if (profile && cartItems) {
      try {
        setIsLoading(true);

        const response = await submitOrder({
          profile: profile,
          cartItems: cartItems,
        });

        if (response.success) {
          toast.success("Order received successfully.", {
            position: "top-right",
            theme: "colored",
          });
          setOrderStatus('success');
          // Clear cached details
          clearContext();
          navigate('/completed');
        } else {
          throw new Error();
        }
      } catch (e) {
        toast.error("Issue receiving order.", {
          position: "top-right",
          theme: "colored",
        });
        setOrderStatus('error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (cartItems == null || profile == null) {
      navigate('/');
    }

    if (profile) {
      setFirstName(profile.account.firstName);
      setLastName(profile.account.lastName);
      setEmail(profile.account.email);
      setPhone(profile.account.phone);

      setConnectionDate(profile.connection.connectionDate);
      setHasCurrentIsp(profile.connection.hasCurrentIsp);
      setCurrentIspName(profile.connection.currentIspName);
      setCurrentIspAccount(profile.connection.currentIspAccount);
    }
  }, []);

  return (
      <div className='grow'>
        <div className="p-4 md:p-8 w-full">
          {(isLoading) && <LoaderFullScreen message="Sending your order..."/>}
          <section className='py-8 lg:py-4'>
            <SectionWrapper>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-fred-purple-500">Sign-Up for FREDNET Residential Broadband</h1>
              <div className='mt-2 sm:mt-4'>
                <p className='text-sm sm:text-lg md:text-xl font-light'>You are signing up for broadband at the address <span
                    className="font-medium">{cartItems?.address?.fullAddress}</span> - <span
                    className="font-bold underline cursor-pointer"
                    onClick={() => resetAddress()}>CHANGE</span></p>
              </div>
            </SectionWrapper>
          </section>
          {cartItems && (
              <section>
                <div className='grid lg:grid-cols-3 items-start mx-auto max-w-screen-2xl gap-8'>
                  <div className='lg:col-span-2 space-y-4'>
                    <div className='space-y-8'>
                      {orderStatus === 'error' && (
                          <div
                              className="w-full m-auto flex flex-col justify-center items-center p-4 text-red-800 border border-red-300 rounded-lg bg-red-50"
                              role="alert">
                            <Info className="inline w-16 h-16" />
                            <h4 className="font-semibold text-2xl">Oh snap!!!</h4>
                            <p className="text-center mt-2 text-fred-purple-500">It looks like something went wrong with
                              placing your order. Please try again. If you are keep having this issue, please get in
                              touch with us.</p>
                          </div>
                      )}
                      <div className="gap-4 grid grid-cols-2">
                        <div className='col-span-full'>
                          <h3 className="text-2xl font-medium text-fred-blue-700">Your Signup Details Summary</h3>
                        </div>
                        <div className="col-span-full sm:col-span-1">
                          <h3 className="block text-lg font-semibold text-fred-purple-500">First Name</h3>
                          <h4 className="mt-2 text-base text-fred-purple-400">{firstName}</h4>
                        </div>
                        <div className="col-span-full sm:col-span-1">
                          <h3 className="block text-lg font-semibold text-fred-purple-500">Last Name</h3>
                          <h4 className="mt-2 text-base text-fred-purple-400">{lastName}</h4>
                        </div>
                        <div className="col-span-full">
                          <h3 className="block text-lg font-semibold text-fred-purple-500">Email</h3>
                          <h4 className="mt-2 text-base text-fred-purple-400">{email}</h4>
                        </div>
                        <div className="col-span-full">
                          <h3 className="block text-lg font-semibold text-fred-purple-500">Mobile Number</h3>
                          <h4 className="mt-2 text-base text-fred-purple-400">{phone}</h4>
                        </div>
                      </div>
                      <div className="gap-4 grid grid-cols-2">
                        <div className='col-span-full'>
                          <h3 className="text-2xl font-medium text-fred-blue-700">Connection Details</h3>
                        </div>
                        <div className="col-span-full">
                          <h3 className="block text-lg font-semibold text-fred-purple-500">Connection Date</h3>
                          <h4 className="mt-2 text-base text-fred-purple-400">{connectionDate}</h4>
                        </div>
                        <div className="col-span-full">
                          <h3 className="block text-lg font-semibold text-fred-purple-500">Current Broadband
                            Provider</h3>
                          <h4 className="mt-2 text-base text-fred-purple-400">{hasCurrentIsp}</h4>
                        </div>
                        {hasCurrentIsp === 'Yes' && (
                            <>
                              <div className="col-span-full">
                                <h3 className="block text-lg font-semibold text-fred-purple-500">Provider's
                                  Name</h3>
                                <h4 className="mt-2 text-base text-fred-purple-400">{currentIspName}</h4>
                              </div>
                              <div className="col-span-full">
                                <h3 className="block text-lg font-semibold text-fred-purple-500">Account
                                  Number</h3>
                                <h4 className="mt-2 text-base text-fred-purple-400">{currentIspAccount}</h4>
                              </div>
                            </>
                        )}
                      </div>
                      {cartItems && cartItems.products && cartItems.products.filter(p => p.type === 'PROMO').length > 0 && (
                          <div className="gap-4 grid grid-cols-2">
                            <div className='col-span-full'>
                              <h3 className="text-2xl font-medium text-fred-blue-700">Promo Code Details</h3>
                            </div>
                            {cartItems.products.filter(p => p.type === 'PROMO').map((promoItem, index) => (
                              <React.Fragment key={promoItem.productId}>
                                <div className="col-span-full">
                                  <h3 className="block text-lg font-semibold text-fred-purple-500">Code</h3>
                                  <h4 className="mt-2 text-base text-green-800 font-bold">{promoItem.promoCode?.promo_code}</h4>
                                </div>
                                <div className="col-span-full">
                                  <h3 className="block text-lg font-semibold text-fred-purple-500">Description</h3>
                                  <h4 className="mt-2 text-base text-fred-purple-400">{promoItem.promoCode?.external_description}</h4>
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                      )}
                    </div>
                    <div className="gap-4 grid grid-cols-2">
                      <div className='col-span-full mb-2'>
                        <p className="text-base font-medium">By signing up, you agree to the FREDNET <a href="https://www.frednet.co.nz/terms-and-conditions" target="_blank"
                                                                                                        className="font-medium text-fred-blue-500">Terms
                          and Conditions</a> and <a href="https://www.frednet.co.nz/privacy-policy" target="_blank" className="font-medium text-fred-blue-500">Privacy
                          Policy</a></p>
                      </div>
                    </div>
                    <div className="w-full space-y-2">
                      <ButtonAlternative text="Change My Details" disabled={isLoading}
                                         onClick={() => navigate('/contact-details')}/>
                      <ButtonPrimary text="Submit Order" disabled={isLoading}
                                     onClick={() => onSubmitOrder()}/>
                    </div>
                  </div>
                  <YourCart cart={cartItems} hideButton onChangeAddress={() => resetAddress()}/>
                </div>
              </section>
          )}
        </div>
        <div className="sticky bottom-0 z-50 w-full flex lg:hidden">
          {cartItems && (
              <YourBottomCart cart={cartItems} hideButton onChangeAddress={() => resetAddress()}/>
          )}
        </div>
      </div>
  );
};

export default Summary;
